import React, { useState } from "react";
import useCommonNavigate from "../../Hooks/useCommonNavigate";
import { loginValidationSchema } from "../Common/Validation/validationSchema";
import { ReactComponent as FreelaLogo } from "../../Images/freela-icons/freela-logo.svg";
import { ReactComponent as UserIcon } from "../../Images/freela-icons/user-icon.svg";
import { ReactComponent as EyeIcon } from "../../Images/freela-icons/eye-icon.svg";
import { ReactComponent as EyeOffIcon } from "../../Images/freela-icons/eyeoff-icon.svg";
import { ReactComponent as LinkedinIcon } from "../../Images/freela-icons/linkedin-icon.svg";
import { ReactComponent as GoogleIcon } from "../../Images/freela-icons/google-icon.svg";
import { ReactComponent as FaceBookIcon } from "../../Images/freela-icons/fb-icon.svg";
import LoginImg from "../../Images/freela-icons/login-left-img.svg";
import { Formik, Field, Form as FormikForm, ErrorMessage } from "formik";
import ApiCall from "../../Api/ApiCall";
import { Row, Col, Image, Button, Form, Stack } from "react-bootstrap";
import { useAuth } from "../../Context/AuthContext";
import { useRole } from "../../Context/RoleContext";
import useToast from "../../Hooks/useToast";
import Loader from "../Common/Loader/Loader";
import getDeviceName from "../Common/utils/getDeviceName";

const Login = ({ openRegister, openForgotPwd }) => {
  const [showPassword, setShowPassword] = useState(false);
  const [keepMeLoggedIn, setKeepMeLoggedIn] = useState(false);
  const { notify } = useToast();
  const { commonNavigate } = useCommonNavigate();
  const { login } = useAuth();
  const { hydrateFromUser } = useRole();
  const [loading, setLoading] = useState(false);

  const API_BASE = process.env.REACT_APP_API_BASE_URL;

  // Function to handle login
  const handleSubmit = async (values) => {
    setLoading(true); // Set loading state to true
    try {
      const payload = {
        email: values.email,
        password: values.password,
        rememberMe: keepMeLoggedIn,
        deviceName: getDeviceName(),
      };

      const response = await ApiCall(
        "POST",
        "user/login",
        payload,
        "application/json",
      );

      if (response.statusCode === 200) {
        localStorage.setItem("keepMeLoggedIn", keepMeLoggedIn);
        const token = response.data.token;
        // Access token stays in memory (see Api/tokenStore). The refresh token
        // is already in an httpOnly cookie set by the server, so nothing about
        // the session needs to be mirrored into localStorage.
        localStorage.setItem("userId", response.data.userId);
        localStorage.setItem(
          "freelancerPercentage",
          response.data.freelancerPercentage,
        );
        localStorage.setItem(
          "clientPercentage",
          response.data.clientPercentage,
        );
        localStorage.setItem("Services", response.data.Services);
        localStorage.setItem("services", response.data.services);
        localStorage.setItem("projects", response.data.projects);
        // Start the session in whichever mode the server last recorded.
        hydrateFromUser(response.data);
        login(token);
        notify("success", "Login successful!");
        response.data.services === 0 &&
        response.data.projects === 0 &&
        response.data.freelancerPercentage === 0 &&
        response.data.clientPercentage === 0
          ? commonNavigate("/welcome-layout")
          : commonNavigate("/find-works");
      } else {
        notify("error", response.message || "Login failed. Please try again.");
      }
    } catch (error) {
      // console.log(error, error.response.status , "error response status code");

      if (error.response.status === 404) {
        notify("error", error.response.data.message || "User Does't exists");
      } else if (error.response.status === 401) {
        notify(
          "error",
          error.response.data.message || "Invalid User and/or Password",
        );
      } else {
        notify("error", "Login failed. Please try again.");
      }
    } finally {
      setLoading(false); // Reset loading state after submission
    }
  };

  const handleSocialLogin = (provider) => {
    try {
      window.location.href = `${API_BASE}auth/${provider}`;
    } catch (error) {
      console.error("Error during social login:", error);
      notify("error", "An error occurred. Please try again.");
    }
  };
  return (
    <>
      {loading && <Loader />}
      <Row className="login_form">
        {/* Left Section */}
        <Col sm={12} md={12} lg={5} xl={5}>
          <FreelaLogo className="ms-lg-4 mt-lg-2" width={260} />
          <div className="d-flex align-items-center justify-content-center mt-5">
            <Image src={LoginImg} alt="LoginImg" className="h-400" fluid />
          </div>
        </Col>

        {/* Right Section */}
        <Col sm={12} md={12} lg={7} xl={7}>
          <h1 className="fs-4 mb-0">Welcome back,</h1>
          <p>
            Don’t have an account?
            <Button
              variant="link"
              className="py-0 ps-1 text-decoration-none freela-text-primary"
              onClick={openRegister}
            >
              Create new account
            </Button>
          </p>

          {/* Login Form with Formik */}

          <Formik
            initialValues={{ email: "", password: "" }}
            validationSchema={loginValidationSchema}
            onSubmit={handleSubmit}
          >
            {({ values, touched }) => {
              const password = values.password || "";

              const rules = {
                length: password.length >= 8 && password.length <= 16,
                uppercase: /[A-Z]/.test(password),
                lowercase: /[a-z]/.test(password),
                number: /[0-9]/.test(password),
                special: /[^A-Za-z0-9]/.test(password),
              };

              const allRulesSatisfied = Object.values(rules).every(
                (rule) => rule === true,
              );

              return (
                <FormikForm>
                  {/* Email Field */}
                  <Form.Group className="mb-3">
                    <Form.Label>
                      Email ID <span className="text-danger">*</span>
                    </Form.Label>
                    <div className="position-relative right-icon">
                      <Field
                        name="email"
                        type="email"
                        className="form-control"
                        placeholder="Enter email"
                      />
                      <UserIcon
                        className="position-absolute top-50 end-0 translate-middle-y me-3"
                        width={20}
                      />
                    </div>
                    <ErrorMessage
                      name="email"
                      component="small"
                      className="text-danger small"
                    />
                  </Form.Group>

                  {/* Password Field */}
                  <Form.Group className="mb-3">
                    <Form.Label>
                      Password <span className="text-danger">*</span>
                    </Form.Label>
                    <div className="position-relative right-icon">
                      <Field
                        name="password"
                        type={showPassword ? "text" : "password"}
                        className="form-control appearance-none"
                        placeholder="Enter password"
                      />
                      <Button
                        variant="link"
                        className="text-decoration-none position-absolute top-50 end-0 translate-middle-y p-0 me-3"
                        onClick={() => setShowPassword(!showPassword)}
                      >
                        {showPassword ? (
                          <EyeIcon width={20} />
                        ) : (
                          <EyeOffIcon width={20} />
                        )}
                      </Button>
                    </div>

                    {/* Live Rules */}
                    {(touched.password || values.password?.length > 0) &&
                      !allRulesSatisfied && (
                        <ul className="list-unstyled mt-1">
                          <li
                            className={`small ${rules.length ? "text-success" : "text-danger"}`}
                          >
                            {rules.length ? "✔" : "✖"} Minimum 8–16 characters
                          </li>
                          <li
                            className={`small ${rules.uppercase ? "text-success" : "text-danger"}`}
                          >
                            {rules.uppercase ? "✔" : "✖"} At least one uppercase
                            letter
                          </li>
                          <li
                            className={`small ${rules.lowercase ? "text-success" : "text-danger"}`}
                          >
                            {rules.lowercase ? "✔" : "✖"} At least one lowercase
                            letter
                          </li>
                          <li
                            className={`small ${rules.number ? "text-success" : "text-danger"}`}
                          >
                            {rules.number ? "✔" : "✖"} At least one number
                          </li>
                          <li
                            className={`small ${rules.special ? "text-success" : "text-danger"}`}
                          >
                            {rules.special ? "✔" : "✖"} At least one special
                            character
                          </li>
                          {/* <ErrorMessage
                          name="password"
                          component="li"
                          className="text-danger small"
                        /> */}
                        </ul>
                      )}
                  </Form.Group>

                  {/* Keep Me Logged In & Forgot Password */}
                  <Stack
                    direction="horizontal"
                    className="justify-content-between mb-3"
                  >
                    <Form.Group controlId="keepMeLoggedIn">
                      <Field
                        as={Form.Check}
                        type="checkbox"
                        name="keepMeLoggedIn"
                        className="me-2"
                        checked={keepMeLoggedIn}
                        onChange={() => setKeepMeLoggedIn(!keepMeLoggedIn)}
                        label="Keep me login"
                      />
                    </Form.Group>
                    <Button
                      variant="link"
                      className="freela-text-primary text-decoration-none p-0"
                      onClick={openForgotPwd}
                    >
                      Forgot Password
                    </Button>
                  </Stack>

                  {/* Social Login */}
                  <Form.Group className="my-4 py-3 position-relative px-5">
                    <div className="px-2 bg-white position-absolute top-50 start-50 translate-middle">
                      <span className="opacity-75">or Login with</span>
                    </div>
                    <div
                      className="text-decorator"
                      style={{ borderTop: "2px dotted #858282" }}
                    ></div>
                  </Form.Group>

                  <Stack
                    direction="horizontal"
                    className="social-icons justify-content-center gap-3"
                  >
                    <Button
                      variant="link"
                      className="freela-text-white text-decoration-none"
                      onClick={() => handleSocialLogin("linkedin")}
                    >
                      <LinkedinIcon width={25} height={25} />
                    </Button>
                    <Button
                      variant="link"
                      className="freela-text-primary text-decoration-none"
                      onClick={() => handleSocialLogin("google")} // Replace with actual Google user email
                    >
                      <GoogleIcon width={25} height={25} />
                    </Button>
                    <Button
                      variant="link"
                      className="freela-text-white text-decoration-none"
                      onClick={() => handleSocialLogin("facebook")}
                    >
                      <FaceBookIcon width={25} height={25} />
                    </Button>
                  </Stack>

                  {/* Login Button */}
                  <Form.Group className="text-center mt-5 w-250px d-grid mx-auto">
                    <Button
                      type="submit"
                      className="freela-btn-primary px-5 mt-3"
                    >
                      Login
                    </Button>
                  </Form.Group>
                </FormikForm>
              );
            }}
          </Formik>
        </Col>
      </Row>
    </>
  );
};

export default Login;
